import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import './TravelPackages.css';
import { AuthContext } from '../context/AuthContext';

// "23 Sep 2026" / "11:02 am" for the Created column
const formatCreatedAt = (value) => {
  if (!value) return { date: '—', time: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '—', time: '' };
  return {
    date: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })
  };
};

const TravelPackages = () => {
  const { hasPermission } = useContext(AuthContext);
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingStatus, setTogglingStatus] = useState({});

  const [searchQuery, setSearchQuery] = useState('');
  // ?status=pending (from the dashboard) opens the list already filtered
  const [searchParams] = useSearchParams();
  const initialStatus = ['active', 'inactive', 'pending'].includes(searchParams.get('status')) ? searchParams.get('status') : 'all';
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [cityFilter, setCityFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(initialStatus !== 'all');

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/travel-packages`);
      setPackages(response.data.data || []);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch travel packages');
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...packages];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(pkg =>
        (pkg.title && pkg.title.toLowerCase().includes(query)) ||
        (pkg.city && pkg.city.toLowerCase().includes(query)) ||
        (pkg.departureCity && pkg.departureCity.toLowerCase().includes(query))
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(pkg => pkg.status === statusFilter);
    }
    if (cityFilter !== 'all') {
      filtered = filtered.filter(pkg => pkg.city === cityFilter);
    }
    setFilteredPackages(filtered);
  }, [packages, searchQuery, statusFilter, cityFilter]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const getUniqueCities = () => {
    const cities = [...new Set(packages.map(p => p.city).filter(Boolean))];
    return cities.sort();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setCityFilter('all');
  };

  const hasActiveFilters = () => {
    return searchQuery.trim() !== '' || statusFilter !== 'all' || cityFilter !== 'all';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this travel package?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/admin/travel-packages/${id}`);
      setPackages(packages.filter(p => p._id !== id));
    } catch (err) {
      alert('Failed to delete travel package');
    }
  };

  const handleToggleStatus = async (pkgId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setTogglingStatus(prev => ({ ...prev, [pkgId]: true }));
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/travel-packages/${pkgId}`, {
        status: newStatus
      }, { headers: { 'Content-Type': 'application/json' } });
      setPackages(packages.map(p =>
        p._id === pkgId ? { ...p, status: response.data.data.status } : p
      ));
    } catch (err) {
      alert('Failed to update travel package status');
    } finally {
      setTogglingStatus(prev => ({ ...prev, [pkgId]: false }));
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_BASE_URL_WITHOUT_API}${imagePath}`;
  };

  const formatDuration = (pkg) => {
    if (pkg.numberOfNights != null && pkg.numberOfDays != null) {
      return `${pkg.numberOfNights}N / ${pkg.numberOfDays}D`;
    }
    if (pkg.numberOfNights != null) return `${pkg.numberOfNights}N`;
    if (pkg.numberOfDays != null) return `${pkg.numberOfDays}D`;
    return '—';
  };

  const pagination = usePagination(filteredPackages, { noun: 'packages' });

  if (loading) {
    return (
      <Layout>
        <div className="travel-packages-container">
          <div className="travel-packages-header">
            <div className="travel-packages-header-content">
              <h1>Travel Packages</h1>
              <p className="travel-packages-subtitle">Manage holiday & travel packages</p>
            </div>
            {hasPermission('travel-packages.create') && (
              <Link to="/sy-admin/travel-packages/new" className="btn btn-primary btn-add">
                <span className="btn-icon">+</span>
                Add New Package
              </Link>
            )}
          </div>
          <div className="travel-packages-table-container">
            <table className="travel-packages-table">
              <thead>
                <tr>
                  <th>Joined</th>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Phone</th>
                  <th>Actions</th>
                  <th>Status</th>
                  <th>Destination</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '90px', height: '14px' }}></div>
                      <div className="skeleton skeleton-text" style={{ width: '60px', height: '12px', marginTop: '6px' }}></div>
                    </td>
                    <td><div className="skeleton skeleton-image" style={{ width: '60px', height: '60px', borderRadius: '8px' }} /></td>
                    <td><div className="skeleton skeleton-text" style={{ width: '60%', height: '16px' }} /></td>
                    <td><div className="skeleton skeleton-text" style={{ width: '100px', height: '14px' }}></div></td>
                    <td><div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }} /></td>
                    <td><div className="skeleton skeleton-switch" style={{ width: '80px', height: '26px' }} /></td>
                    <td><div className="skeleton skeleton-text" style={{ width: '50%', height: '14px' }} /></td>
                    <td><div className="skeleton skeleton-text" style={{ width: '40%', height: '14px' }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="travel-packages-container">
        <div className="travel-packages-header">
          <div className="travel-packages-header-content">
            <h1>Travel Packages</h1>
            <p className="travel-packages-subtitle">Manage holiday & travel packages</p>
          </div>
          {hasPermission('travel-packages.create') && (
            <Link to="/sy-admin/travel-packages/new" className="btn btn-primary btn-add">
              <span className="btn-icon">+</span>
              Add New Package
            </Link>
          )}
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {packages.length > 0 && (
          <div className="filters-section">
            <div className="filters-header">
              <button
                className="filters-toggle-btn"
                onClick={() => setShowFilters(!showFilters)}
              >
                <span className="filters-icon">🔍</span>
                Filters
                {hasActiveFilters() && (
                  <span className="active-filters-badge">
                    {[searchQuery.trim(), statusFilter, cityFilter].filter(v => v !== '' && v !== 'all').length}
                  </span>
                )}
                <span className="toggle-arrow">{showFilters ? '▲' : '▼'}</span>
              </button>
              {hasActiveFilters() && (
                <button className="clear-filters-btn" onClick={clearFilters}>Clear All</button>
              )}
            </div>
            {showFilters && (
              <div className="filters-content">
                <div className="filter-row">
                  <div className="filter-group">
                    <label className="filter-label">Search</label>
                    <input
                      type="text"
                      className="filter-input"
                      placeholder="Search by title, city or departure..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="filter-group">
                    <label className="filter-label">Status</label>
                    <select
                      className="filter-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label className="filter-label">City</label>
                    <select
                      className="filter-select"
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                    >
                      <option value="all">All Cities</option>
                      {getUniqueCities().map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {hasActiveFilters() && (
                  <div className="filter-results-info">
                    Showing {filteredPackages.length} of {packages.length} packages
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {packages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">✈️</div>
            <h2>No travel packages found</h2>
            <p>Get started by adding your first travel package.</p>
            {hasPermission('travel-packages.create') && (
              <Link to="/sy-admin/travel-packages/new" className="btn btn-primary">Add Your First Package</Link>
            )}
          </div>
        ) : (
          <div className="travel-packages-table-container">
            {filteredPackages.length === 0 && hasActiveFilters() ? (
              <div className="empty-state">
                <div className="empty-state-icon">🔍</div>
                <h2>No packages match your filters</h2>
                <p>Try adjusting your filter criteria or clear all filters.</p>
                <button onClick={clearFilters} className="btn btn-primary">Clear Filters</button>
              </div>
            ) : (
              <table className="travel-packages-table">
                <thead>
                  <tr>
                    <th>Joined</th>
                    <th>Image</th>
                    <th>Title</th>
                    <th>Phone</th>
                    <th>Actions</th>
                    <th>Status</th>
                    <th>Destination</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.items.map((pkg) => {
                    const isTogglingStatus = togglingStatus[pkg._id];
                    const featuredImage = pkg.featuredImage || (pkg.images && pkg.images[0]);
                    const imageUrl = getImageUrl(featuredImage);
                    const phoneNumber = pkg.phone || pkg.whatsappNumber || '';
                    const createdAt = formatCreatedAt(pkg.createdAt);
                    return (
                      <tr key={pkg._id}>
                        <td>
                          <div className="hotel-created-cell">
                            <span className="created-date">{createdAt.date}</span>
                            <span className="created-time">{createdAt.time}</span>
                          </div>
                        </td>
                        <td>
                          <div className="pkg-image-cell">
                            {imageUrl ? (
                              <img src={imageUrl} alt={pkg.title} className="pkg-table-image" />
                            ) : (
                              <div className="pkg-image-placeholder">
                                <span className="placeholder-icon-small">✈️</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="pkg-name-cell">
                            <span className="pkg-name-text">{pkg.title}</span>
                          </div>
                        </td>
                        <td>
                          <div className="hotel-phone-cell">
                            {phoneNumber ? <a href={`tel:${phoneNumber}`}>{phoneNumber}</a> : <span className="category-empty">—</span>}
                          </div>
                        </td>
                        <td>
                          <div className="table-actions">
                            <Link
                              to={`/sy-admin/travel-packages/view/${pkg._id}`}
                              className="btn btn-edit btn-icon-only"
                              title="View Package"
                            >
                              <span className="btn-icon">👁️</span>
                            </Link>
                            {hasPermission('travel-packages.update') && (
                              <Link
                                to={`/sy-admin/travel-packages/edit/${pkg._id}`}
                                className="btn btn-edit btn-icon-only"
                                title="Edit Package"
                              >
                                <span className="btn-icon">✏️</span>
                              </Link>
                            )}
                            {hasPermission('travel-packages.delete') && (
                              <button
                                onClick={() => handleDelete(pkg._id)}
                                className="btn btn-delete btn-icon-only"
                                title="Delete Package"
                              >
                                <span className="btn-icon">🗑️</span>
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="status-cell">
                            <label className="status-toggle-label">
                              <div className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={pkg.status === 'active'}
                                  onChange={() => handleToggleStatus(pkg._id, pkg.status)}
                                  disabled={isTogglingStatus}
                                  className="toggle-input"
                                />
                                <span className={`toggle-slider ${isTogglingStatus ? 'loading' : ''}`} />
                              </div>
                              <span className={`status-text ${pkg.status === 'active' ? 'status-active-text' : 'status-inactive-text'}`}>
                                {pkg.status === 'active' ? 'Active' : pkg.status === 'inactive' ? 'Inactive' : 'Pending'}
                              </span>
                            </label>
                          </div>
                        </td>
                        <td>
                          <div className="pkg-city-cell">
                            <span className="location-icon">📍</span>
                            <span>{pkg.city || 'N/A'}</span>
                            {pkg.departureCity && (
                              <span className="departure-badge">Ex-{pkg.departureCity}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="pkg-duration-cell">{formatDuration(pkg)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <Pagination {...pagination.props} />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TravelPackages;
